import * as React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faArrowLeft, faCheck, faClock, faCompass, faLightbulb } from '@fortawesome/free-solid-svg-icons'
import { Link, Navigate, useParams } from 'react-router-dom'
import ChatbotWrapper from '../../../../components/ChatbotWrapper'
import DiscordLink from '../../../../components/DiscordLink'
import { guideMap } from '../guides'
import styles from './styles.module.scss'
import { RainbowText } from '../../../../components/RainbowText'

const Guide: React.FC = () => {
  const { guide: guideSlug } = useParams()
  const guide = guideSlug ? guideMap.get(guideSlug) : undefined

  React.useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [guideSlug])

  if (!guide) {
    return <Navigate to='/chatbot/learn' replace />
  }

  const related = guide.related.map((slug) => guideMap.get(slug)).filter(Boolean)

  return (
    <ChatbotWrapper
      decorate
      title={guide.title}
      titleButtons={
        <Link to='/chatbot/learn' className={styles.backButton} title='Back to Learn'>
          <FontAwesomeIcon icon={faArrowLeft as IconDefinition} />
        </Link>
      }
      header={
        <>
          <div className={styles.heroTag}>{guide.tag}</div>
          <p className='display-6'>{guide.summary}</p>
          <div className={styles.meta}>
            <span>
              <FontAwesomeIcon icon={faCompass as IconDefinition} /> {guide.level}
            </span>
            <span>
              <FontAwesomeIcon icon={faClock as IconDefinition} /> About {guide.time}
            </span>
          </div>
        </>
      }
    >
      <div className={styles.guideLayout}>
        <aside className={styles.sideBar}>
          <div className={styles.sideCard}>
            <div className={styles.sideTitle}>In this guide</div>
            <a href='#outcome'>What you&apos;ll build</a>
            {guide.sections.map((section) => (
              <a href={`#${section.id}`} key={section.id}>
                {section.title}
              </a>
            ))}
          </div>
        </aside>

        <article className={styles.article}>
          <section id='outcome' className={styles.outcomeCard}>
            <div className={styles.sectionHeading}>
              <FontAwesomeIcon icon={faCheck as IconDefinition} />
              <h2>What you&apos;ll build</h2>
            </div>
            <ul>
              {guide.outcome.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          {guide.sections.map((section, sectionIndex) => (
            <section id={section.id} className={styles.guideSection} key={section.id}>
              <div className={styles.guideSectionTitle}>
                <div className={styles.stepNumber}>{String(sectionIndex + 1).padStart(2, '0')}</div>
                <h2>{section.title}</h2>
              </div>

              {section.paragraphs?.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}

              {section.steps ? (
                <ol className={styles.steps}>
                  {section.steps.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              ) : null}

              {section.preview ? <div className={styles.preview}>{section.preview}</div> : null}

              {section.note ? (
                <div className={styles.note}>
                  <FontAwesomeIcon icon={faLightbulb as IconDefinition} />
                  <div>{section.note}</div>
                </div>
              ) : null}
            </section>
          ))}

          {related.length ? (
            <section className={styles.relatedSection}>
              <h2>Keep going</h2>
              <div className={styles.relatedGrid}>
                {related.map((relatedGuide) =>
                  relatedGuide ? (
                    <Link to={`/chatbot/learn/${relatedGuide.slug}`} className={styles.relatedCard} key={relatedGuide.slug}>
                      <FontAwesomeIcon icon={relatedGuide.icon} />
                      <div>
                        <span>{relatedGuide.tag}</span>
                        <strong>{relatedGuide.title}</strong>
                      </div>
                    </Link>
                  ) : null,
                )}
              </div>
            </section>
          ) : null}

          <div className={styles.helpFooter}>
            <strong>Something in the guide not matching what you see?</strong>
            <span>
              <RainbowText text='Draco.Bot' /> changes over time. Come ask in the <DiscordLink /> and we&apos;ll help you sort it out.
            </span>
          </div>
        </article>
      </div>
    </ChatbotWrapper>
  )
}

export default Guide
